import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import Holidays from 'date-holidays';
import moment from 'moment';

import { Contact } from '../../../models/contact.model';
import { Service } from '../../../models/service.model';
import { CalendarHeaderComponent } from '../../../shared/components/calendar-header/calendar-header.component';

@Component({
  selector: 'app-ticket-tob',
  templateUrl: './ticket-tob.component.html',
  styleUrls: ['../../containers/create-ticket/create-ticket.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TicketTOBComponent implements OnInit {
  public tobTxGroup: UntypedFormGroup = this.fb.group({
    reporter: this.fb.control('', Validators.required),
    preferredContactMethod: this.fb.control('phone', Validators.required),
    countryCode: this.fb.control(''),
    newAccountName: this.fb.control('', Validators.required),
    newFEIN: this.fb.control('', Validators.required),

    newAddress: this.fb.group({
      street1: ['', Validators.required],
      street2: [''],
      city: ['', Validators.required],
      state: ['', Validators.required],
      zip: ['', Validators.required],
    }),

    desiredDate: this.fb.control(null, [
      Validators.required,
      this.validateDisconnectDate.bind(this),
    ]),
    additionalComment: this.fb.control(''),
    services: this.fb.array([
      this.fb.group({
        identifyIssueBy: this.fb.control('location'),
        serviceId: this.fb.control(null, Validators.required),
        locationId: this.fb.control(null, Validators.required),
        locationAddress: this.fb.control(null),
        servicesByLocation: this.fb.array([
          this.fb.group({
            id: this.fb.control(''),
            disabled: this.fb.control(''),
            nit: this.fb.control(''),
            product: this.fb.control(''),
            locationId: this.fb.control(''),
            locationName: this.fb.control(''),
          }),
        ]),
      }),
    ]),
  });

  @Input() services: Service[];

  @Input() selectedServiceId: string;

  @Input() isLoading: boolean;

  @Input() isLoaded: boolean;

  @Input() submissionError: boolean;

  @Input() errorMessage: string;

  @Input() isSubmitting: boolean;

  @Input() primaryReporters: Contact[];

  @Output() submitForm = new EventEmitter();

  @Output() dateChange: EventEmitter<MatDatepickerInputEvent<any>>;

  formSubmitAttempted = false;

  inlineErrors: any = {};

  uniqueLocations: any[] = [];

  allServices: any[] = [];

  isLoadingServices = true;

  loadingUsers = true;

  customCalHeader = CalendarHeaderComponent;

  startCalendarAt = new Date();

  holidays = [
    `New Year's Day`,
    `Memorial Day`,
    `Independence Day`,
    `Labor Day`,
    `Thanksgiving Day`,
    `Christmas Day`,
  ];

  constructor(private fb: UntypedFormBuilder) {}

  ngOnInit() {
    if (this.services) {
      this.isLoadingServices = false;
      this.allServices = this.services;
      const serviceObject = {};
      const m = new Map();

      // eslint-disable-next-line no-restricted-syntax
      for (const service of this.services) {
        serviceObject[service.Id] = service;
        // If service has location, create array.
        if (service.Z_Location__r && !m.has(service.Z_Location__c)) {
          m.set(service.Z_Location__c, true);
          this.uniqueLocations.push(service);
        }
      }
    }

    this.startCalendarAt = this.getNextValidDate();

    this.canSubmit();
  }

  // eslint-disable-next-line prettier/prettier
  validateDisconnectDate(c: UntypedFormControl): {
    validateDate: { valid: boolean };
  } {
    const formDate = c.value ? new Date(c.value) : null;
    const isValid = formDate ? this.filterDates(formDate) : false;
    const errorOutput = {
      validateDate: {
        valid: false,
      },
    };
    return isValid ? null : errorOutput;
  }

  // initialize the calendar to start at the first valid date
  getNextValidDate(): Date {
    const dateToCheck = moment(new Date()).add(0, 'days');
    while (!this.filterDates(dateToCheck.toDate())) {
      dateToCheck.add(1, 'days');
    }
    return dateToCheck.toDate();
  }

  filterDates = (d: Date): boolean => {
    const day = d?.getDay();
    // Prevent Saturday and Sunday from being selected.
    const weekday = day !== 0 && day !== 6;
    // A user cannot transfer on holidays
    const holiday = this.isHoliday(d);
    // A user can only select the day after the current date.
    const tomorrow = moment(new Date()).add(0, 'days');
    const afterTomorrowDays = d > tomorrow.toDate();

    return weekday && !holiday && afterTomorrowDays;
  };

  isHoliday(calendarDate: Date): boolean {
    const allHolidays = new Holidays('US');
    const holiday = allHolidays.isHoliday(calendarDate);
    return holiday && this.holidays.indexOf(holiday[0].name) >= 0;
  }

  canSubmit(): boolean {
    const defaultMessage = 'This is a required field';

    // eslint-disable-next-line no-restricted-syntax
    for (const prop in this.tobTxGroup.controls) {
      // eslint-disable-next-line no-prototype-builtins
      if (this.tobTxGroup.controls.hasOwnProperty(prop)) {
        const control: UntypedFormControl = this.tobTxGroup.controls[
          prop
        ] as UntypedFormControl;
        if (!control.valid) {
          this.inlineErrors[prop] = defaultMessage;
        } else {
          delete this.inlineErrors[prop];
        }
        control?.updateValueAndValidity();
        if (control.errors && control.errors.pattern) {
          this.inlineErrors[prop] = 'pattern';
        } else if (control.errors && prop === 'desiredDate') {
          this.inlineErrors[prop] = control.errors.required
            ? defaultMessage
            : 'date';
        }
      }
    }

    return this.tobTxGroup.valid;
  }

  submitTicket(): void {
    if (this.canSubmit()) {
      // submit the ticket
      const request = {
        order_type: 'Transfer_Of_Billing_Order',
        contactId: this.tobTxGroup.value.reporter,
        preferredContactMethod: this.tobTxGroup.value.preferredContactMethod, // eslint-disable-line

        newAccountName: this.tobTxGroup.value.newAccountName,
        newFEIN: this.tobTxGroup.value.newFEIN,
        newAddress: {
          street1: this.tobTxGroup.get('newAddress.street1').value,
          street2: this.tobTxGroup.get('newAddress.street2').value,
          city: this.tobTxGroup.get('newAddress.city').value,
          state: this.tobTxGroup.get('newAddress.state').value,
          zipCode: this.tobTxGroup.get('newAddress.zip').value,
        },

        desiredDate: this.tobTxGroup.value.desiredDate,
        additionalComment: this.tobTxGroup.value.additionalComment,
        lineItems: this.tobTxGroup.value.services,
      };
      this.submitForm.emit(request);
    } else {
      this.formSubmitAttempted = true;
    }
  }

  usersLoaded(): void {
    this.loadingUsers = false;
  }

  servicesLoaded(): void {
    this.isLoadingServices = false;
  }
}
